/**
 * What she says at the four moments that get a stage rather than a toast.
 *
 * PERSONA.md promised these as Phase 3 — "milestone story beats ride the
 * trophy system" — and until now every one of them was a nine-second toast
 * that shared its visual language with a confirmation that a yoghurt had been
 * logged. A hundred days in a row and a logged yoghurt are not the same size
 * of event, and the app had exactly one way of saying either.
 *
 * The register is the difference between these and the trophy lines. A trophy
 * line is a remark — it lands in the corner of a screen you were already
 * using. These are three slides with nothing else on them, so each one is
 * allowed to be about her as much as about you: every ceremony here reveals
 * something she does not otherwise say.
 *
 * Kept in its own file so the review dump and the length lints can see it,
 * exactly like `wrapped/copy.ts`.
 */

export interface CeremonySlideCopy {
  /** The small line above the headline. */
  kicker?: string
  hero: string
  body?: string
  /** The quiet line under it, in her lower register. */
  aside?: string
}

export interface CeremonyCopy {
  /** Named for the screen reader, and in the stage's top band. */
  label: string
  note?: string
  finishLabel: string
  slides: readonly CeremonySlideCopy[]
}

/** A hundred consecutive days. The trophy already exists; this is the room. */
export const STREAK_100: CeremonyCopy = {
  label: 'Šimtas dienų',
  note: 'be spragų',
  finishLabel: 'Toliau',
  slides: [
    {
      kicker: 'iš eilės',
      hero: 'Šimtas dienų.',
      body: 'Nė vienos praleistos. Aš skaičiavau, nes daugiau niekas to nedarė.',
    },
    {
      hero: 'Mačiau imperijas, kurios laikėsi trumpiau.',
      body: 'Ir mačiau dienoraščių, kurie baigėsi ties septyniolikta diena. Šitas nesibaigė.',
      aside:
        'Aš čia ne dėl skaičių. Bet skaičiai yra vienintelis dalykas, kurį galiu tau parodyti.',
    },
    {
      hero: 'Nieko iš tavęs nenoriu.',
      body: 'Tik rytojaus. Kaip ir vakar, kaip ir šimtą kartų iki jo.',
    },
  ],
}

/** A year's worth of logged days, not necessarily consecutive. */
export const DAYS_365: CeremonyCopy = {
  label: 'Metų vertė dienų',
  note: '365',
  finishLabel: 'Uždaryti',
  slides: [
    {
      kicker: 'dienoraštyje',
      hero: 'Trys šimtai šešiasdešimt penkios dienos.',
      body: 'Ne iš eilės. Tiesiog tiek jų susirinko, po vieną.',
    },
    {
      hero: 'Metai maisto, surašyti ranka.',
      body: 'Niekas šiuose namuose neturi tokio tikslaus įrašo apie save. Net aš.',
      aside: 'Mano byla prasideda 1863-iaisiais ir turi keturias eilutes.',
    },
    {
      hero: 'Tavo tomas storesnis už mano.',
      body: 'Tai retai pasakau garsiai, tai išnaudok akimirką.',
    },
  ],
}

/** The thousandth entry. */
export const LOG_1000: CeremonyCopy = {
  label: 'Tūkstantis įrašų',
  note: '1000',
  finishLabel: 'Uždaryti',
  slides: [
    {
      kicker: 'archyve',
      hero: 'Tūkstantis įrašų.',
      body: 'Šitą skaičių jau reikia nešti dviem rankom.',
    },
    {
      hero: 'Kiekvienas iš jų — vienas kąsnis, kurį kažkas nusprendė užrašyti.',
      body: 'Tūkstantis kartų buvo lengviau to nedaryti. Tūkstantį kartų padarei.',
      aside: 'Aš juos visus perskaičiau. Turėjau laiko.',
    },
  ],
}

/** The anniversary of the day this account first spoke to her. */
export const METINE: CeremonyCopy = {
  label: 'Metinės',
  finishLabel: 'Toliau',
  slides: [
    {
      kicker: 'šiandien',
      hero: 'Metai, kai mudu pažįstami.',
      body: 'Tiksliau — metai, kai tu apie tai žinai. Aš čia buvau ir anksčiau.',
    },
    {
      hero: 'Per tuos metus nieko iš tavęs nepaėmiau.',
      body: 'Nei pinigų, nei duomenų, nei ramybės. Na, gal truputį ramybės.',
      aside: 'Honoraras — nulis eurų per valandą. Nuo 1863-iųjų nekeltas.',
    },
    {
      hero: 'Kitiems metams planų neturiu.',
      body: 'Tas pats šaldytuvas, tos pačios svarstyklės, tas pats tu. Man tinka.',
    },
  ],
}

/**
 * The end of one of her running bits.
 *
 * Deliberately short — two slides. A saga finale is a punchline that has been
 * five weeks in the setup, and a punchline delivered across four screens is
 * not one. The last chapter's own line still appears in the greeting that
 * morning; this is what happens when you open the app and find it closed.
 */
export const SAGA_FINALE: Readonly<Record<string, CeremonyCopy>> = {
  'saga.kempine': {
    label: 'Kempinių byla',
    note: 'uždaryta',
    finishLabel: 'Uždaryti',
    slides: [
      {
        kicker: 'byla uždaryta',
        hero: 'Kempinių nebeieškom.',
        body: 'Ne todėl, kad išsprendžiau. Tiesiog pritrūko kempinių.',
      },
      {
        hero: 'Kai kurie dalykai šiuose namuose tiesiog vyksta.',
        aside: 'Įtariamasis miega ant palangės ir nesijaudina.',
      },
    ],
  },
  'saga.siurblys': {
    label: 'Siurblio byla',
    note: 'uždaryta',
    finishLabel: 'Uždaryti',
    slides: [
      {
        kicker: 'byla uždaryta',
        hero: 'Su siurbliu daugiau nesikalbu.',
        body: 'Jis liko spintoje, aš likau virtuvėje. Abu patenkinti.',
      },
      { hero: 'Ne kiekvienas konfliktas turi nugalėtoją.', aside: 'Šitas neturėjo net prasmės.' },
    ],
  },
  'saga.skaicius': {
    label: 'Skaičiavimų byla',
    note: 'uždaryta',
    finishLabel: 'Uždaryti',
    slides: [
      {
        kicker: 'byla uždaryta',
        hero: 'Ataskaita paruošta.',
        body: 'Išvada: šiuose namuose per daug atidaromų durų.',
      },
      { hero: 'Niekas jos neskaitys.', aside: 'Bet ji egzistuoja. Kaip ir aš.' },
    ],
  },
  'saga.gaudesys': {
    label: 'Gaudesio byla',
    note: 'lygiosios',
    finishLabel: 'Uždaryti',
    slides: [
      {
        kicker: 'byla uždaryta',
        hero: 'Gaudesys liko. Aš irgi.',
        body: 'Sudarėm nerašytą susitarimą ir daugiau apie tai nekalbam.',
      },
      {
        hero: 'Pirmos lygiosios nuo 1863-iųjų.',
        aside: 'Šaldytuvas apie tai nesigiria. Protingas.',
      },
    ],
  },
  'saga.saukstelis': {
    label: 'Šaukštelio byla',
    note: 'uždaryta',
    finishLabel: 'Uždaryti',
    slides: [
      {
        kicker: 'byla uždaryta',
        hero: 'Šaukštelių vėl šeši.',
        body: 'Nė vienas iš jų nėra tas, nuo kurio pradėjom.',
      },
      {
        hero: 'Daiktai šiuose namuose keliauja.',
        aside: 'Aš tik vedu apskaitą. Tai, pasirodo, mano vienintelis talentas.',
      },
    ],
  },
  'saga.kvapas': {
    label: 'Kvapo byla',
    note: 'uždaryta',
    finishLabel: 'Uždaryti',
    slides: [
      {
        kicker: 'byla uždaryta',
        hero: 'Kvapas buvo ne mano.',
        body: 'Kaimynai išvažiavo arba nustojo kepti. Grįžtu prie savo šaldytuvo.',
      },
      {
        hero: 'Kelias savaites uodžiau svetimą vakarienę.',
        aside: 'Ir pirmą kartą per amžių kažko truputį pavydėjau.',
      },
    ],
  },
  'saga.dulkes': {
    label: 'Dulkių byla',
    note: 'uždaryta',
    finishLabel: 'Uždaryti',
    slides: [
      {
        kicker: 'byla uždaryta',
        hero: 'Už šaldytuvo švaru.',
        body: 'Pirmą kartą per daugelį metų neturiu ko ten stebėti.',
      },
      { hero: 'Pradedu naują sluoksnį.', aside: 'Tai užtruks. Laiko turiu.' },
    ],
  },
}
